import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { rememberBillBranch, useBranches } from '../context/BranchContext';
import { demoPosPath } from '../demoPosPaths';
import api from '../services/api';

/**
 * Open (or create) the bill for a queue entry, or start a blank walk-in bill.
 * Asks for the branch only when it isn't already known: a check-in carries its own branch,
 * and with exactly one branch ticked that one is used.
 */
export default function useStartBill(onChanged) {
  const navigate = useNavigate();
  const { selectedBranches, branchName } = useBranches();
  const [picking, setPicking] = useState(null); // { kind: 'queue', entry } | { kind: 'manual' }
  const [busy, setBusy] = useState(false);

  const openBill = useCallback((id) => navigate(demoPosPath(`bills/${id}`)), [navigate]);

  const create = useCallback(
    async (job, franchiseId) => {
      setBusy(true);
      try {
        if (franchiseId) rememberBillBranch(franchiseId);
        if (job.kind === 'manual') {
          const bill = await api.createBill({ franchise_id: franchiseId, franchise_name: branchName(franchiseId), source: 'manual' });
          openBill(bill.id);
          return;
        }
        const { entry } = job;
        const res = await api.billFromQueue({
          source: entry.source,
          source_ref: entry.source_ref,
          franchise_id: franchiseId || entry.franchise_id,
        });
        if (res.unmatched?.length) {
          toast(`No price found for: ${res.unmatched.join(', ')}. Pick it from the price list.`, { duration: 6000 });
        }
        onChanged?.();
        openBill(res.bill.id);
      } catch (e) {
        toast.error(e.message || 'Could not create the bill');
      } finally {
        setBusy(false);
        setPicking(null);
      }
    },
    [branchName, onChanged, openBill],
  );

  const start = useCallback(
    (job) => {
      if (job.kind === 'queue' && job.entry.bill) {
        openBill(job.entry.bill.id);
        return;
      }
      const known = job.kind === 'queue' ? job.entry.franchise_id : null;
      if (known) {
        create(job, known);
      } else if (selectedBranches.length === 1) {
        create(job, String(selectedBranches[0].id));
      } else {
        setPicking(job);
      }
    },
    [create, openBill, selectedBranches],
  );

  return {
    start,
    busy,
    picking,
    pick: (franchiseId) => picking && create(picking, franchiseId),
    cancelPick: () => setPicking(null),
  };
}
